import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Flame, ShieldAlert, Utensils } from "lucide-react";
import { BioButton } from "@/components/bio/BioButton";
import { EmptyState } from "@/components/bio/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/format";

const RLABEL = { gluten: "Gluten", lacteos: "Lácteos", frutos_secos: "Frutos secos", vegetariano: "Vegetariano" };

export default function MenusView() {
    const [cafs, setCafs] = useState([]);
    const [caf, setCaf] = useState("");
    const [menu, setMenu] = useState(null);
    const [products, setProducts] = useState([]);
    const [addOpen, setAddOpen] = useState(false);

    useEffect(() => {
        api.get("/dash/filters").then(({ data }) => {
            setCafs(data.cafeterias);
            if (data.cafeterias.length) setCaf(data.cafeterias[0].id);
        });
        api.get("/products").then(({ data }) => setProducts(data));
    }, []);

    const load = useCallback(() => {
        if (!caf) return;
        setMenu(null);
        api.get("/menus", { params: { cafeteria_id: caf } }).then(({ data }) => setMenu(data));
    }, [caf]);

    useEffect(() => { load(); }, [load]);

    const addProduct = async (p) => {
        try {
            await api.post("/menus", { cafeteria_id: caf, product_id: p.id });
            toast.success(`${p.name} añadido al menú`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo añadir");
        }
    };
    const removeItem = async (m) => {
        await api.delete(`/menus/${m.id}`);
        setMenu((x) => x.filter((i) => i.id !== m.id));
    };

    const inMenu = new Set((menu || []).map((m) => m.product_id));

    return (
        <div className="space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Menús y nutrición</h1>
                    <p className="text-bio-text-secondary mt-1 text-sm">Planifica el menú del día por cafetería con información nutricional.</p>
                </div>
                <div className="flex gap-2">
                    <Select value={caf} onValueChange={setCaf}>
                        <SelectTrigger data-testid="menu-cafeteria" className="h-11 rounded-bio bg-white border-[1.5px] min-w-[180px]"><SelectValue /></SelectTrigger>
                        <SelectContent>{cafs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <BioButton data-testid="menu-add-open" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" /> Añadir</BioButton>
                </div>
            </div>

            {!menu ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 rounded-bio-card" />)}</div>
            ) : menu.length === 0 ? (
                <div className="bg-white rounded-bio-card border border-bio-border-subtle"><EmptyState icon={Utensils} title="Menú vacío" description="Añade productos al menú del día." action={<BioButton variant="secondary" size="sm" onClick={() => setAddOpen(true)}>Añadir producto</BioButton>} /></div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {menu.map((m) => (
                        <div key={m.id} data-testid={`menu-item-${m.product_id}`} className="bg-white rounded-bio-card border border-bio-border-subtle overflow-hidden shadow-bio-sm">
                            <img src={m.image} alt={m.name} className="h-32 w-full object-cover" />
                            <div className="p-4">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="font-bold text-sm">{m.name}</p>
                                        <p className="text-xs text-bio-text-muted">{m.category} · {formatUSD(m.price)}</p>
                                    </div>
                                    <button data-testid={`menu-remove-${m.product_id}`} onClick={() => removeItem(m)} className="text-bio-text-muted hover:text-bio-error"><Trash2 className="h-4 w-4" /></button>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold bg-[#FEF6E9] text-[#B36B00] px-2 py-0.5 rounded-full"><Flame className="h-3 w-3" /> {m.calories} kcal</span>
                                    {m.restrictions?.map((r) => (
                                        <span key={r} className="inline-flex items-center gap-1 text-xs font-semibold bg-[#FEF3C7] text-[#B45309] px-2 py-0.5 rounded-full"><ShieldAlert className="h-3 w-3" /> {RLABEL[r] || r}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="rounded-bio-lg max-w-lg border-bio-border-subtle">
                    <DialogHeader>
                        <DialogTitle>Añadir al menú</DialogTitle>
                        <DialogDescription>Selecciona productos del catálogo para la cafetería.</DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[50vh] overflow-y-auto space-y-1.5">
                        {products.map((p) => (
                            <div key={p.id} className="flex items-center gap-3 rounded-bio border border-bio-border-subtle p-2.5">
                                <img src={p.image} alt="" className="h-10 w-10 rounded-bio object-cover" />
                                <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{p.name}</p><p className="text-xs text-bio-text-muted">{p.category} · {formatUSD(p.price)}</p></div>
                                <BioButton size="sm" variant={inMenu.has(p.id) ? "ghost" : "secondary"} disabled={inMenu.has(p.id)} data-testid={`menu-pick-${p.id}`} onClick={() => addProduct(p)}>
                                    {inMenu.has(p.id) ? "En menú" : "Añadir"}
                                </BioButton>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
